import React, { useState, useEffect, useRef } from "react";
import { FaBus, FaClock, FaCalendarAlt } from "react-icons/fa";
import { busApi } from "../../services/api/busApi";
import { useVehicleTracking } from "../../hooks/useVehicleTracking";
import { resolveGtfsRouteId } from "../../utils/gtfsRouteMapping";

const toRad = (deg) => (deg * Math.PI) / 180;

const distanceMetersBetween = (a, b) => {
  // Haversine (suficiente e estável para distâncias pequenas)
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
};

const resolveNextStopName = (vehicle, stops) => {
  if (!vehicle) return null;
  if (!Array.isArray(stops) || stops.length === 0) return null;
  if (typeof vehicle.lat !== "number" || typeof vehicle.lng !== "number") {
    return null;
  }

  const stopsWithCoords = stops.filter(
    (s) => typeof s?.lat === "number" && typeof s?.lng === "number"
  );
  if (stopsWithCoords.length === 0) return null;

  let nearestStop = null;
  let nearestDist = Number.POSITIVE_INFINITY;
  for (const stop of stopsWithCoords) {
    const d = distanceMetersBetween(
      { lat: vehicle.lat, lng: vehicle.lng },
      { lat: stop.lat, lng: stop.lng }
    );
    if (d < nearestDist) {
      nearestDist = d;
      nearestStop = stop;
    }
  }

  if (!nearestStop) return null;

  // Se tivermos sequência, tenta pegar a próxima
  const nearestSeq =
    typeof nearestStop.sequence === "number" ? nearestStop.sequence : null;
  if (nearestSeq !== null) {
    const stopsBySeq = stopsWithCoords
      .filter((s) => typeof s.sequence === "number")
      .slice()
      .sort((a, b) => a.sequence - b.sequence);

    const next =
      stopsBySeq.find((s) => s.sequence === nearestSeq + 1) ||
      stopsBySeq.find((s) => s.sequence > nearestSeq) ||
      null;

    if (next?.stopName) return next.stopName;
  }

  return nearestStop.stopName || null;
};

function PartidasLinha({
  routeId,
  routeShortName,
  onBack,
  onVehiclesUpdate,
  onVehicleClick,
}) {
  const [partidas, setPartidas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedTrip, setSelectedTrip] = useState(0);
  const [vehiclesExpanded, setVehiclesExpanded] = useState(false);

  const gtfsRouteId = React.useMemo(() => {
    return resolveGtfsRouteId(routeShortName ?? partidas?.shortName ?? routeId);
  }, [routeShortName, partidas?.shortName, routeId]);

  const tripStopsCacheRef = useRef(new Map());
  const [nextStopByVehicleId, setNextStopByVehicleId] = useState({});
  const [nextStopLoadingByVehicleId, setNextStopLoadingByVehicleId] = useState(
    {}
  );

  const allVehicleFilters = React.useMemo(() => {
    if (!gtfsRouteId) return {};
    return { routeId: gtfsRouteId };
  }, [gtfsRouteId]);

  const { vehicles: vehiclesFromApi, loading: vehiclesLoading } = useVehicleTracking(
    allVehicleFilters,
    15000,
    !!gtfsRouteId
  );

  const getVehicleDirectionText = React.useCallback((vehicle) => {
    const label = vehicle?.directionLabel || null;
    const terminus = vehicle?.directionTerminus || null;

    if (label && terminus) return `${label} → ${terminus}`;
    if (label) return String(label);

    const numeric = Number(vehicle?.directionId);
    if (numeric === 0) return "IDA";
    if (numeric === 1) return "VOLTA";

    return "Sentido indefinido";
  }, []);

  const dayCategories = React.useMemo(() => {
    if (!partidas?.dayCategories) return [];
    const cats = Array.isArray(partidas.dayCategories) ? partidas.dayCategories : [];
    return cats;
  }, [partidas]);

  // Manter índices válidos quando o filtro mudar
  useEffect(() => {
    setSelectedCategory(0);
    setSelectedTrip(0);
  }, [routeId, partidas]);

  const vehicles = React.useMemo(() => {
    const list = Array.isArray(vehiclesFromApi) ? vehiclesFromApi : [];

    // Segurança: garantir que routeId bate com o selecionado (GTFS)
    if (!gtfsRouteId) return list;
    return list.filter((v) => String(v?.routeId || "") === String(gtfsRouteId));
  }, [
    vehiclesFromApi,
    gtfsRouteId,
  ]);

  // Notificar componente pai sobre veículos
  useEffect(() => {
    if (onVehiclesUpdate) {
      onVehiclesUpdate(vehicles);
    }
  }, [vehicles, onVehiclesUpdate]);

  useEffect(() => {
    if (!vehiclesExpanded) return;
    if (!Array.isArray(vehicles) || vehicles.length === 0) return;

    let cancelled = false;

    const isNonEmptyString = (v) =>
      typeof v === "string" && v.trim().length > 0;

    const loadNextStops = async () => {
      const nextStopUpdates = {};
      const loadingUpdates = {};

      for (const vehicle of vehicles) {
        const vehicleId = vehicle?.vehicleId;
        if (!vehicleId) continue;

        const tripId =
          (isNonEmptyString(vehicle?.tripId) && vehicle.tripId.trim()) ||
          (isNonEmptyString(vehicle?.tripIdRaw) && vehicle.tripIdRaw.trim()) ||
          null;

        if (!isNonEmptyString(tripId)) {
          nextStopUpdates[vehicleId] = null;
          loadingUpdates[vehicleId] = false;
          continue;
        }

        const cachedStops = tripStopsCacheRef.current.get(tripId);
        if (cachedStops) {
          nextStopUpdates[vehicleId] = resolveNextStopName(
            vehicle,
            cachedStops
          );
          loadingUpdates[vehicleId] = false;
          continue;
        }

        loadingUpdates[vehicleId] = true;
        if (!cancelled) {
          setNextStopLoadingByVehicleId((prev) => ({
            ...prev,
            [vehicleId]: true,
          }));
        }

        try {
          const res = await busApi.getTripShape(tripId, { includeStops: true });
          const stops = Array.isArray(res?.data?.stops) ? res.data.stops : [];
          tripStopsCacheRef.current.set(tripId, stops);
          nextStopUpdates[vehicleId] = resolveNextStopName(vehicle, stops);
        } catch (err) {
          console.error("Erro ao buscar paradas do trip para próxima parada:", {
            err,
            tripId,
            vehicleId,
          });
          nextStopUpdates[vehicleId] = null;
        } finally {
          loadingUpdates[vehicleId] = false;
        }
      }

      if (cancelled) return;

      setNextStopByVehicleId((prev) => ({
        ...prev,
        ...nextStopUpdates,
      }));

      setNextStopLoadingByVehicleId((prev) => ({
        ...prev,
        ...loadingUpdates,
      }));
    };

    loadNextStops();

    return () => {
      cancelled = true;
    };
  }, [vehiclesExpanded, vehicles]);

  useEffect(() => {
    if (!routeId) return;

    const fetchPartidas = async () => {
      setLoading(true);
      setError("");
      setPartidas(null);
      try {
        const response = await busApi.getDeparturesByRoute(routeId);
        setPartidas(response.data);
        setSelectedCategory(0);
        setSelectedTrip(0);
      } catch (err) {
        setError("Não foi possível carregar os horários.");
      } finally {
        setLoading(false);
      }
    };

    fetchPartidas();
  }, [routeId]);

  if (!routeId) {
    return null;
  }

  const safeCategoryIndex = Math.min(selectedCategory, Math.max(0, dayCategories.length - 1));
  const currentCategory = dayCategories?.[safeCategoryIndex];
  const currentTrip = currentCategory?.trips[selectedTrip];

  return (
    <div className="sheet-partidas-linha-container">
      <button onClick={onBack} className="back-button">
        ← Voltar
      </button>

      {loading && (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <div className="loading-spinner" aria-hidden="true" />
          <p style={{ color: "#6b7280", marginTop: "16px" }}>
            Carregando horários...
          </p>
        </div>
      )}

      {error && (
        <div
          style={{
            textAlign: "center",
            padding: "20px",
            background: "#fef2f2",
            borderRadius: "8px",
            color: "#ef4444",
          }}
        >
          <p>❌ {error}</p>
        </div>
      )}

      {partidas && (
        <div>
          {/* HEADER DA LINHA */}
          <div className="linha-header">
            <div
              className="linha-badge"
              style={{ backgroundColor: partidas.color || "#6366f1" }}
            >
              {partidas.shortName}
            </div>
            <div className="linha-info">
              <h2>{partidas.longName}</h2>
              <p>
                {dayCategories.length}{" "}
                {dayCategories.length === 1
                  ? "categoria"
                  : "categorias"}{" "}
                de dia
              </p>
            </div>
          </div>

          {/* Informação de ônibus em tempo real */}
          <div
            style={{
              padding: "12px",
              background: vehicles.length > 0 ? "#f0f9ff" : "#fef2f2",
              borderRadius: "8px",
              marginBottom: "16px",
              border:
                vehicles.length > 0 ? "1px solid #bae6fd" : "1px solid #fecaca",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: vehicles.length > 0 ? "pointer" : "default",
              }}
              onClick={() =>
                vehicles.length > 0 && setVehiclesExpanded(!vehiclesExpanded)
              }
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span style={{ fontSize: "20px" }}>🚌</span>
                <div>
                  {vehicles.length > 0 ? (
                    <>
                      <strong style={{ color: "#0369a1" }}>
                        {vehicles.length}{" "}
                        {vehicles.length === 1
                          ? "ônibus em circulação"
                          : "ônibus em circulação"}
                      </strong>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        {vehiclesLoading
                          ? "Atualizando..."
                          : "Clique para ver detalhes"}
                      </div>
                    </>
                  ) : (
                    <>
                      <strong style={{ color: "#dc2626" }}>
                        Nenhum ônibus em circulação
                      </strong>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        {vehiclesLoading
                          ? "Buscando..."
                          : "Sem dados em tempo real no momento"}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {vehicles.length > 0 && (
                <span style={{ fontSize: "18px", color: "#0369a1" }}>
                  {vehiclesExpanded ? "▲" : "▼"}
                </span>
              )}
            </div>

            {/* Lista de veículos expansível */}
            {vehiclesExpanded && vehicles.length > 0 && (
              <div
                style={{
                  marginTop: "12px",
                  borderTop: "1px solid #bae6fd",
                  paddingTop: "12px",
                }}
              >
                {vehicles.map((vehicle, index) => {
                  const vehicleDisplayId =
                    vehicle.vehiclePrefix ??
                    vehicle.vehicleId ??
                    vehicle.plate ??
                    null;

                  return (
                  <div
                    key={vehicle.vehicleId || index}
                    style={{
                      padding: "8px",
                      background: "white",
                      borderRadius: "6px",
                      marginBottom: index < vehicles.length - 1 ? "8px" : "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid #e0f2fe",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#0c4a6e",
                          marginBottom: "4px",
                        }}
                      >
                        🚍 Veículo #{vehicleDisplayId || "-"}
                      </div>
                      <div style={{ fontSize: "12px", color: "#334155" }}>
                        {getVehicleDirectionText(vehicle)}
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        {nextStopLoadingByVehicleId[vehicle.vehicleId]
                          ? "🛑 Buscando próxima parada..."
                          : nextStopByVehicleId[vehicle.vehicleId]
                          ? `🛑 Próxima parada: ${
                              nextStopByVehicleId[vehicle.vehicleId]
                            }`
                          : "🛑 Próxima parada indisponível"}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onVehicleClick) {
                          onVehicleClick(vehicle);
                        }
                      }}
                      style={{
                        padding: "6px 10px",
                        background: "#0369a1",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        whiteSpace: "nowrap",
                        width: "auto",
                        flexShrink: 0,
                      }}
                    >
                      📍 Ver no mapa
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ABAS DE CATEGORIA DE DIA */}
          <div className="category-tabs">
            {dayCategories.map((category, index) => (
              <button
                key={category.category}
                className={`category-tab ${
                  safeCategoryIndex === index ? "active" : ""
                }`}
                onClick={() => {
                  setSelectedCategory(index);
                  setSelectedTrip(0);
                }}
              >
                {category.categoryName}
              </button>
            ))}
          </div>

          {/* ABAS DE SENTIDO */}
          {currentCategory && currentCategory.trips.length > 1 && (
            <div className="trip-tabs">
              {currentCategory.trips.map((trip, index) => (
                <button
                  key={trip.tripId}
                  className={`trip-tab ${
                    selectedTrip === index ? "active" : ""
                  }`}
                  onClick={() => setSelectedTrip(index)}
                >
                  <FaBus /> {trip.tripHeadsign}
                </button>
              ))}
            </div>
          )}

          {/* GRID DE HORÁRIOS */}
          {currentTrip && (
            <div className="horarios-section">
              {currentCategory.trips.length === 1 && (
                <h3 className="sentido-title">
                  <FaBus /> Sentido: {currentTrip.tripHeadsign}
                </h3>
              )}

              <div className="horarios-info">
                <span>
                  <FaCalendarAlt /> {currentCategory.categoryName}
                </span>
                <span>
                  <FaClock /> {currentTrip.departures.length} horários
                </span>
              </div>

              <div className="horarios-grid">
                {currentTrip.departures.map((departure, index) => {
                  const time = departure.departureTime.substring(0, 5);
                  const now = new Date();
                  const currentTime = `${String(now.getHours()).padStart(
                    2,
                    "0"
                  )}:${String(now.getMinutes()).padStart(2, "0")}`;
                  const isNext =
                    time > currentTime &&
                    index ===
                      currentTrip.departures.findIndex(
                        (d) => d.departureTime.substring(0, 5) > currentTime
                      );

                  return (
                    <div
                      key={departure.departureTime}
                      className={`horario-card ${isNext ? "next" : ""}`}
                    >
                      {time}
                      {isNext && <span className="next-badge">Próximo</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PartidasLinha;
